import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
// import io from 'socket.io-client';
import { getSocket } from '../utils/Socket';
import './SolarSystem.css';
import { formatDistanceToNow } from 'date-fns';
import Planet3DRenderer from './solarsystem_kppredictor/Planet3DRenderer';
import PlanetDetailCard from './solarsystem_kppredictor/PlanetDetailedCard';
import { PLANET_DATA } from '../utils/PlanetConstants';
import { cleanupAudioContext } from './solarsystem_kppredictor/AudioContextSingleton';
import PlanetPreloader from './preloaders/PlanetPreloader';
import {planetAssets, earthAssets, missionAssets, backgroundAssets, timelineAssets} from './preloaders/PlanetPreloader';

import AsteroidPreloader from './preloaders/AsteroidPreLoader';
import {asteroidAssets} from './preloaders/AsteroidPreLoader';
import AsteroidBelt3D from './solarsystem_kppredictor/AsteroidBelt3D';


import { computeOrbitPathAU, ORBITAL_ELEMENTS } from '../utils/OrbitalElements';
import renderBeltHighlight from './solarsystem_kppredictor/RenderBeltHighlight';

import { normalizeAndSplitAssets } from '../utils/normalizeAndSplitAssets';
// import MilkyWayIntro from './solarsystem_kppredictor/MilkyWayIntro';
const MilkyWayIntro = lazy(() => import('./solarsystem_kppredictor/MilkyWayIntro'));

const AU_TO_PIXELS = 15;
const SCALE_FACTOR = 30;
const SUN_ACTUAL_DIAMETER_KM = 1.39e6;

const PLANET_ACTUAL_DIAMETERS_KM = {
  Mercury: 4879 * SCALE_FACTOR,
  Venus: 12104 * SCALE_FACTOR,
  Earth: 12742 * SCALE_FACTOR,
  Moon: 3475 * SCALE_FACTOR,
  Mars: 6779 * SCALE_FACTOR,
  Jupiter: 139820 * SCALE_FACTOR,
  Saturn: 116460 * SCALE_FACTOR,
  Uranus: 50724 * SCALE_FACTOR,
  Neptune: 49244 * SCALE_FACTOR,
  Pluto: 2376.6 * SCALE_FACTOR,

   // Kuiper Belt Objects
  Eris: 2326 * SCALE_FACTOR,
  Haumea: 1632 * SCALE_FACTOR,      // Mean diameter (highly elongated)
  Makemake: 1434 * SCALE_FACTOR,
  Quaoar: 1110 * SCALE_FACTOR,
  Sedna: 995 * SCALE_FACTOR,
  Orcus: 910 * SCALE_FACTOR,
  Ixion: 617 * SCALE_FACTOR,
  "2002 TX300": 286 * SCALE_FACTOR,
  Salacia: 846 * SCALE_FACTOR,
  Gonggong: 1230 * SCALE_FACTOR,
  "2002 MS4": 934 * SCALE_FACTOR,
  Lempo: 272 * SCALE_FACTOR,        // Primary component
  Varda: 740 * SCALE_FACTOR,
  Varuna: 668 * SCALE_FACTOR,
  Chaos: 600 * SCALE_FACTOR,
  "2002 AW197": 768 * SCALE_FACTOR,
  "2002 UX25": 650 * SCALE_FACTOR,
  "2002 TC302": 584 * SCALE_FACTOR,
  "2003 AZ84": 727 * SCALE_FACTOR,
};

const SUN_SCALE_FACTOR = 1 / SUN_ACTUAL_DIAMETER_KM;

// const PLANET_DIAMETERS = Object.fromEntries(
//   Object.entries(PLANET_ACTUAL_DIAMETERS_KM).map(([planet, diameter]) => [
//     planet,
//     diameter * SUN_SCALE_FACTOR,
//   ])
// );

function SolarSystem() {
  const PLANET_DIAMETERS = useMemo(() => {
    return Object.fromEntries(
      Object.entries(PLANET_ACTUAL_DIAMETERS_KM).map(([planet, diameter]) => [
        planet,
        diameter * SUN_SCALE_FACTOR,
      ])
    );
  }, []);

  const [isConnected, setIsConnected] = useState(false);
  const previousPlanetsRef = useRef(null);

  const [planetPositions, setPlanetPositions] = useState([]);
  const [sunPosition, setSunPosition] = useState(null);
  const [sunSize, setSunSize] = useState(600);
  const [hovered, setHovered] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);


  const [kpIndex, setKpIndex] = useState(null);
  const [KpRetrievalTime, setRetrievalTime] = useState(null);
  const [scale, setScale] = useState(getDynamicScale());

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [lastPan, setLastPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panOrigin, setPanOrigin] = useState({ x: 0, y: 0 });
  

  const lastTouchDistanceRef = useRef(null);
  const lastMidpointRef = useRef(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  const [quickMenuOpen, setQuickMenuOpen] = useState(false); 
  const [planetsOpen, setPlanetsOpen] = useState(true);
  const [kuiperOpen, setKuiperOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const kuiperListRef = useRef(null);
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const scrollStartRef = useRef(0);
  const dragMovedRef = useRef(false);
  const pressedBodyRef = useRef(null);

  const [is3DViewEnabled, setIs3DViewEnabled] = useState(false);
  const [show3DContainer, setShow3DContainer] = useState(false);
  const [are2DPlanetsVisible, setAre2DPlanetsVisible] = useState(true);
  const [fadeOutComplete, setFadeOutComplete] = useState(false);
  const [is3DFadingOut, setIs3DFadingOut] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isTransitioningRef = useRef(false);
  
  const [isExpanded, setIsExpanded] = useState(false); // planet card
  const [enableZoomTranslation, setEnableZoomTranslation] = useState(false);

  const [ready, setReady] = useState(false);
  const [planetTextures, setPlanetTextures] = useState({});
  const [ringTextures, setRingTextures] = useState({});
  const [missionImages, setMissionImages] = useState({});
  const [backgroundImages, setBackgroundImages] = useState({});
  const [timelineImages, setTimelineImages] = useState({});

  const [planetModel, setPlanetModel] = useState({});
  const [asteroidGeometries, setAsteroidGeometries] = useState([]);

  const [containerSize, setContainerSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [isTrackingSatellite, setIsTrackingSatellite] = useState(false);
  const [isExploringIss, setisExploringIss] = useState(false);
  const [isExploringHst, setisExploringHst] = useState(false);
  const [isExploringMars, setIsExploringMars] = useState(false);

  const [selectedPlanet, setSelectedPlanet] = useState(null);
  const [skipAnimation, setSkipAnimation] = useState(null);
  const [skipAnimationSetting, setSkipAnimationSetting] = useState(false); 
  const [milkywayAnimStarted, setMilkywayAnimStarted] = useState(false);

  // socket
  // useEffect(() => {
  //   const socket = getSocket();
  //   let hasRequested = false;
  //   const requestInitialData = () => {
  //     if (!hasRequested && socket.connected) {
  //       socket.emit('requestInitialData');
  //       hasRequested = true;
  //     }
  //   };
  //   const onConnect = () => {
  //     setIsConnected(true);
  //     requestInitialData();
  //   };

  //   const onDisconnect = () => {
  //     setIsConnected(false);
  //     hasRequested = false; // Allow future reconnects to trigger again
  //   };
  //   socket.on('connect', onConnect);
  //   socket.on('disconnect', onDisconnect);

  //   // In case we're already connected
  //   requestInitialData();

  //   return () => {
  //     socket.off('connect', onConnect);
  //     socket.off('disconnect', onDisconnect);
  //   };
  // }, []);

  // scaling
  useEffect(() => {
    const original = {
      overflowX: document.documentElement.style.overflowX,
      overflowY: document.documentElement.style.overflowY,
      margin: document.body.style.margin,
      padding: document.body.style.padding,
      height: document.body.style.height,
      width: document.body.style.width,
    };

    document.documentElement.style.overflowX = 'hidden';
    document.documentElement.style.overflowY = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100%';
    document.body.style.width = '100%';

    return () => {
      // Restore styles on document.documentElement
      ['overflowX', 'overflowY'].forEach((key) => {
        document.documentElement.style[key] = original[key];
      });

      // Restore styles on document.body
      ['margin', 'padding', 'height', 'width'].forEach((key) => {
        document.body.style[key] = original[key];
      });
    };
  }, []);

  
  useEffect(() => {
    const handleResize = () => {
      setContainerSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // useEffect(() => {
  //   console.log('planetPositions updated:', planetPositions);
  // }, [planetPositions]);

  // loading screen
  //EDIT HERE
  // useEffect(() => {
  //   console.log("LOADING...");
  //   console.log(Object.keys(planetPositions).length);
  //   if (Object.keys(planetPositions).length > 0) {
  //     requestAnimationFrame(() => {
  //       console.log("LOADING COMPLETE");
  //       setLoading(false);
  //     });
  //   }
  // }, [planetPositions]);

 // socket connection updata kp and planet data. make updates here to speed up the recall. 
  // useEffect(() => {
  //   const socket = getSocket();
  //   const planetNameMap = {
  //     1: "Mercury",
  //     2: "Venus",
  //     3: "Earth",
  //     4: "Mars",
  //     5: "Jupiter",
  //     6: "Saturn",
  //     7: "Uranus",
  //     8: "Neptune",
  //     10: "Sun"
  //   };

  // const handlePlanetPositions = (dataObj) => {
  //   if (!dataObj || typeof dataObj !== 'object') return;

  //   // Extract actual planet data object
  //   // const planets = dataObj.planet_data;
  //   const planets = dataObj;
  //   if (!planets || typeof planets !== 'object') return;

  //   console.log('[FRONTEND] Raw planet data received from socket:', dataObj);

  //   const namedPlanets = Object.entries(planets).reduce((acc, [id, planet]) => {
  //     const name = planetNameMap[id] || `Planet_${id}`;
  //     let x, y, z;

  //     if (Array.isArray(planet.position) && planet.position.length >= 3) {
  //       [x, y, z] = planet.position;
  //     } else if (
  //       planet.position &&
  //       typeof planet.position === 'object' &&
  //       typeof planet.position.x === 'number' &&
  //       typeof planet.position.y === 'number' &&
  //       typeof planet.position.z === 'number'
  //     ) {
  //       ({ x, y, z } = planet.position);
  //     } else {
  //       return acc;
  //     }

  //     acc[name] = {
  //       name,
  //       position: { x, y, z },
  //       orbital_speed: planet.orbital_speed,
  //       distanceFromSun: planet.distanceFromSun,
  //     };
  //     // console.log("distance from Sun:", planet.distanceFromSun, "Type:", typeof planet.distanceFromSun);

  //     return acc;
  //   }, {});

  //   setPlanetPositions(namedPlanets);
  // };

  //   const handleSunData = (data) => {
  //     if (data?.position) {
  //       setSunPosition(data.position);
  //       setSunSize(data.diameter || 600);
  //     }
  //   };

  //   const handleKpIndex = (data) => {
  //     if (Array.isArray(data?.kp_prediction) && typeof data.kp_prediction[0] === 'number') {
  //       setKpIndex(parseFloat(data.kp_prediction[0]));
  //     }
  //     if (data?.retrievalTimeUTC) {
  //       setRetrievalTime(data.retrievalTimeUTC);
  //     }
  //   };

  //   const handleConnectError = () => console.error("WebSocket connection error");
  //   // if (socket && !socket.connected && socket.disconnected) {
  //   //   socket.connect();
  //   // }

  //   const handleConnect = () => {
  //     console.log('Connected to WebSocket:', socket.id);
  //     socket.emit('requestInitialData'); // request server data after connect
  //   };

  //   const handleDisconnect = () => console.log('Disconnected from WebSocket');
  //   const handleReconnect = () => console.log('Reconnected to WebSocket');
    
  //    // socket.on('connect', () => console.log("Connected to WebSocket"));
  //   socket.on('connect', handleConnect);
  //   socket.on('disconnect', handleDisconnect);
  //   socket.io.on('reconnect', handleReconnect);
  //   socket.on('planetPositions', handlePlanetPositions);
  //   socket.on('sunData', handleSunData);
  //   socket.on('kpIndex', handleKpIndex);
  //   socket.on('connect_error', handleConnectError);

  //   return () => {
  //      socket.off('connect', handleConnect);
  //       socket.off('disconnect', handleDisconnect);
  //       socket.io.off('reconnect', handleReconnect);
  //       socket.off('planetPositions', handlePlanetPositions);
  //       socket.off('sunData', handleSunData);
  //       socket.off('kpIndex', handleKpIndex);
  //       socket.off('connect_error', handleConnectError);
  //   };
  // }, []);
  useEffect(() => {
    const planetNameMap = {
      1: "Mercury",
      2: "Venus",
      3: "Earth",
      301: "Moon",
      4: "Mars",
      5: "Jupiter",
      6: "Saturn",
      7: "Uranus",
      8: "Neptune",
      10: "Sun",
      999: "Pluto",

      // Kuiper Belt Objects
      136199: "Eris",
      136108: "Haumea",
      136472: "Makemake",
      50000: "Quaoar",
      90377: "Sedna",
      90482: "Orcus",
      28978: "Ixion",
      55636: "2002 TX300",
      120347: "Salacia",
      225088: "Gonggong",
      307261: "2002 MS4",
      47171: "Lempo",
      174567: "Varda",
      20000: "Varuna",
      19521: "Chaos",
      55565: "2002 AW197",
      55637: "2002 UX25",
      84522: "2002 TC302",
      208996: "2003 AZ84",
    };

    const fetchData = async () => {
      try {
        const planetRes = await fetch('/data/planet_data.json');
        const fullPlanetJson = await planetRes.json();

        // Correctly extract the actual planet data object
        const planetData = fullPlanetJson.planet_data;
        console.log('[FRONTEND] Loaded planet data from JSON:', planetData);

        // Find sun first
        const sun = planetData["10"];
        if (!sun || !Array.isArray(sun.position)) {
          console.error("Sun position missing or invalid");
          return;
        }
        const [xSun, ySun, zSun] = sun.position;

        // const namedPlanets = Object.entries(planetData).reduce((acc, [id, planet]) => {
        //   const name = planetNameMap[id] || `Planet_${id}`;
        //   let x, y, z;
          
        //   if (Array.isArray(planet.position) && planet.position.length >= 3) {
        //     [x, y, z] = planet.position;
        //   } else if (
        //     planet.position &&
        //     typeof planet.position === 'object' &&
        //     typeof planet.position.x === 'number' &&
        //     typeof planet.position.y === 'number' &&
        //     typeof planet.position.z === 'number'
        //   ) {
        //     ({ x, y, z } = planet.position);
        //   } else {
        //     return acc;
        //   }

        //   // Calculate distance from Sun (just like backend)
        //   const distanceFromSun = Math.sqrt(
        //     (x - xSun) ** 2 +
        //     (y - ySun) ** 2 +
        //     (z - zSun) ** 2
        //   );

        //   acc[name] = {
        //     name,
        //     position: { x, y, z },
        //     orbital_speed: planet.orbital_speed,
        //     distanceFromSun
        //   };

        //   return acc;
        // }, {});

        const MOON_VISUAL_OFFSET = 0.2;
        const namedPlanets = Object.entries(planetData).reduce((acc, [id, planet]) => {
          const name = planetNameMap[id] || `Planet_${id}`;
          let x, y, z;

          if (Array.isArray(planet.position) && planet.position.length >= 3) {
            [x, y, z] = planet.position;
          } else if (
            planet.position &&
            typeof planet.position === 'object' &&
            typeof planet.position.x === 'number' &&
            typeof planet.position.y === 'number' &&
            typeof planet.position.z === 'number'
          ) {
            ({ x, y, z } = planet.position);
          } else {
            return acc;
          }

          // Visual-only offset so Moon isn't hidden inside Earth
          let renderX = x;
          let renderY = y;
          let renderZ = z;

          if (name === "Moon") {
            renderX += MOON_VISUAL_OFFSET;
          }

          const distanceFromSun = Math.sqrt(
            (x - xSun) ** 2 +
            (y - ySun) ** 2 +
            (z - zSun) ** 2
          );

          acc[name] = {
            name,
            position: {
              x: renderX,
              y: renderY,
              z: renderZ,
            },
            // Preserve actual coordinates if needed elsewhere
            actualPosition: { x, y, z },
            orbital_speed: planet.orbital_speed,
            distanceFromSun,
          };

          return acc;
        }, {});

        setPlanetPositions(namedPlanets);

        // Set sun position and size separately
        setSunPosition({ x: xSun, y: ySun, z: zSun });
        setSunSize(sun.diameter || 600);

        // Load KP Index data
        const kpRes = await fetch('/data/kp_data.json');
        const kpData = await kpRes.json();
        // if (Array.isArray(kpData?.kp_prediction) && typeof kpData.kp_prediction[0] === 'number') {
        //   setKpIndex(parseFloat(kpData.kp_prediction[0]));
        // }
         if (Array.isArray(kpData?.latest_kp) && kpData.latest_kp.length > 0) {
          const latestKpValue = kpData.latest_kp[0].kp;
          setKpIndex(parseFloat(latestKpValue));
        }
        if (kpData?.retrieval_time_utc) {
          console.log('[FRONTEND] Loaded kp data from JSON:', kpData);
          setRetrievalTime(kpData.retrieval_time_utc);
        }

      } catch (err) {
        console.error("Failed to load data from JSON:", err);
      }
    };

    fetchData();
  }, []);

  const KUIPER_BELT_OBJECTS = new Set([
    "Pluto",
    "Eris",
    "Haumea",
    "Makemake",
    "Quaoar",
    "Sedna",
    "Orcus",
    "Ixion",
    "2002 TX300",
    "Salacia",
    "Gonggong",
    "2002 MS4",
    "Lempo",
    "Varda",
    "Varuna",
    "Chaos",
    "2002 AW197",
    "2002 UX25",
    "2002 TC302",
    "2003 AZ84",
  ]);

  const planets = Object.values(planetPositions).filter(
    (body) => !KUIPER_BELT_OBJECTS.has(body.name)
  );

  const kuiperBodies = Object.values(planetPositions).filter(
    (body) => KUIPER_BELT_OBJECTS.has(body.name)
  );

  const kuiperInnerR = ORBITAL_ELEMENTS.Neptune.a * AU_TO_PIXELS;
  const kuiperOuterR = 50 * AU_TO_PIXELS;

  const asteroidInnerR = ORBITAL_ELEMENTS.Mars.a * AU_TO_PIXELS;
  const asteroidOuterR = 3.3 * AU_TO_PIXELS;


  // useEffect(() => {
  //   const stored = localStorage.getItem('skipAnimation');
  //   if (stored === 'true') {
  //     setSkipAnimation(true);
  //   } else {
  //     setSkipAnimation(false);
  //   }
  // }, []);
  // 1. Load saved skipAnimation
  useEffect(() => {
    const stored = localStorage.getItem('skipAnimation') === 'true'; // convert to boolean
    setSkipAnimationSetting(stored); // checkbox reflects stored value immediately
    setSkipAnimation(stored);        // skip animation if stored
  }, []);

  // Only used for current session animation skipping
  useEffect(() => {
    if (skipAnimation) {
      // Immediately skip animation on load
      setReady(true);
      setLoading(false);
    }
  }, [skipAnimation]);

  // zoom funcitonalities
  useEffect(() => {
    const stopPanning = () => {
      if (isPanning) {
        setIsPanning(false);
        setLastPan(pan);
      }
    };
    window.addEventListener("mouseup", stopPanning);
    window.addEventListener("touchend", stopPanning);
    return () => {
      window.removeEventListener("mouseup", stopPanning);
      window.removeEventListener("touchend", stopPanning);
    };
  }, [isPanning, pan]);

  useEffect(() => {
    if (!isPanning) {
      setLastPan(pan);
    }
  }, [pan, zoom, isPanning]);

  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);

  useEffect(() => {
    zoomRef.current = zoom;
    panRef.current = pan;
  }, [zoom, pan]);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  const handleResetView = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    animateZoomAndPan(1, { x: 0, y: 0 }, 800, () => {
      if (!is3DViewEnabled) {
        setIsTransitioning(false);
      }
    });

    if (is3DViewEnabled) {
      restore2DView();
      // You can also delay `setIsTransitioning(false)` here if needed
    }
  };


  const handleWheel = useCallback((e) => {
    if (e.target.closest('.menu-category-content')) return;
    if (is3DViewEnabled) {
      handleResetView();
      return;
    }
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;

    const zoomSensitivity = 0.1;
    const delta = e.deltaY < 0 ? 1 : -1;
    const scaleAmount = 1 + delta * zoomSensitivity;

    const rect = el.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const contentX = (mouseX - pan.x - rect.width / 2) / zoom;
    const contentY = (mouseY - pan.y - rect.height / 2) / zoom;

    const newZoom = Math.max(0.3, Math.min(5, zoom * scaleAmount));
    const zoomRatio = newZoom / zoom;

    const newPan = {
      x: pan.x - contentX * (zoomRatio - 1) * zoom,
      y: pan.y - contentY * (zoomRatio - 1) * zoom,
    };

    setZoom(newZoom);
    setPan(newPan);
    setLastPan(newPan);
  }, [pan, zoom, is3DViewEnabled, handleResetView]);

  const handleZoomByStep = (direction) => {
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const simulatedWheelEvent = {
      deltaY: direction < 0 ? -100 : 100,
      preventDefault: () => {},
      clientX: centerX + rect.left,
      clientY: centerY + rect.top,
      target: containerRef.current,
    };

    handleWheel(simulatedWheelEvent);
  };

  // const getPanLimits = () => {
  //   // const maxOrbit = Math.max(...planetPositions.map(getOrbitalRadius)) + 100;
  //   const maxOrbit = Math.max(...Object.values(planetPositions).map(getOrbitalRadius)) + 100;
  //   const buffer = 200; // Add buffer for labels or touch overshoot
  //   const zoomedMax = (maxOrbit + buffer) * zoom;

  //   return {
  //     xMin: -zoomedMax,
  //     xMax: zoomedMax,
  //     yMin: -zoomedMax,
  //     yMax: zoomedMax,
  //   };
  // };
  
  // const clampPan = (newPan) => {
  //   const limits = getPanLimits();
  //   return {
  //     x: Math.max(limits.xMin, Math.min(limits.xMax, newPan.x)),
  //     y: Math.max(limits.yMin, Math.min(limits.yMax, newPan.y)),
  //   };
  // };

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.menu-category-content')) return;
    if (loading || is3DViewEnabled) return;
    e.preventDefault();
    setIsPanning(true);
    setPanOrigin({ x: e.clientX, y: e.clientY });
  }, [loading]);

  // const handleMouseMove = useCallback((e) => {
  //   if (!isPanning || is3DViewEnabled) return;
  //   e.preventDefault();
  //   const newPan = {
  //     x: lastPan.x + (e.clientX - panOrigin.x),
  //     y: lastPan.y + (e.clientY - panOrigin.y),
  //   };
  //   setPan(clampPan(newPan));
  // }, [isPanning, lastPan, panOrigin]);

  let animationFrameId = null;

  const updatePan = (newPan) => {
    if (animationFrameId) return; // already scheduled
    animationFrameId = requestAnimationFrame(() => {
      setPan(newPan);
      animationFrameId = null;
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (e.target.closest('.menu-category-content')) return;
    if (!isPanning || is3DViewEnabled) return;
    e.preventDefault();
    const newPan = {
      x: lastPan.x + (e.clientX - panOrigin.x),
      y: lastPan.y + (e.clientY - panOrigin.y),
    };

     updatePan(clampPan(newPan));
  }, [isPanning, lastPan, panOrigin, is3DViewEnabled]);

  const handleTouchStart = useCallback((e) => {
    if (e.target.closest('.menu-category-content')) return;
    if (e.touches.length === 1) {
      setIsPanning(true);
      setPanOrigin({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      setIsPanning(true);
      lastTouchDistanceRef.current = getTouchDistance(e.touches);

      lastMidpointRef.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2,
      };
    }
  }, [is3DViewEnabled]);

  const handleTouchMove = useCallback((e) => {
    if (e.target.closest('.menu-category-content')) return;
    if (isTransitioning) {
        e.preventDefault();
        return;
    }
    // if (is3DViewEnabled) {
    //   handleResetView();
    //   return;
    // }
    const el = containerRef.current;
    const rect = el.getBoundingClientRect();

    if (e.touches.length === 1 && isPanning) {      
      e.preventDefault();
      const dx = e.touches[0].clientX - panOrigin.x;
      const dy = e.touches[0].clientY - panOrigin.y;
      const newPan = clampPan({ x: lastPan.x + dx, y: lastPan.y + dy });
      setPan(newPan);
      return;
    }

    if (e.touches.length === 2) {
      e.preventDefault();
      const [touch1, touch2] = e.touches;
      // 1. Get screen midpoint
      const screenMidpoint = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      // 2. Convert screen midpoint to world coordinates (pre-zoom)
      const canvasCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };

      const screenToContent = {
        x: (screenMidpoint.x - canvasCenter.x - pan.x) / zoom,
        y: (screenMidpoint.y - canvasCenter.y - pan.y) / zoom,
      };

      // 3. Calculate new zoom
      const newDistance = getTouchDistance(e.touches);
      const zoomChange = newDistance / lastTouchDistanceRef.current;
      const newZoom = Math.max(0.3, Math.min(10, zoom * zoomChange));
      const zoomRatio = newZoom / zoom;

      // 4. Adjust pan to keep that world point stationary under the screen midpoint
      let newPan = {
        x: pan.x - screenToContent.x * (zoomRatio - 1) * zoom,
        y: pan.y - screenToContent.y * (zoomRatio - 1) * zoom,
      };

      // 5. Apply any panning due to midpoint movement
      if (lastMidpointRef.current) {
        const dx = screenMidpoint.x - lastMidpointRef.current.x;
        const dy = screenMidpoint.y - lastMidpointRef.current.y;
        newPan.x += dx;
        newPan.y += dy;
      }

      newPan = clampPan(newPan);

      // 6. Update state
      setZoom(newZoom);
      setPan(newPan);
      setLastPan(newPan);

      lastTouchDistanceRef.current = newDistance;
      lastMidpointRef.current = screenMidpoint;
    }
  }, [isPanning, pan, zoom, lastPan, panOrigin, is3DViewEnabled]);


  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleTouchEnd = () => {
      lastMidpointRef.current = null;
      lastTouchDistanceRef.current = 0;
      setIsPanning(false);
      setLastPan(panRef.current);
    };

    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  useEffect(() => {
    if (is3DViewEnabled) return
    const el = containerRef.current;
    if (!el) return;

    // Mouse
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('mousemove', handleMouseMove);

    // Touch (with passive: false)
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleWheel); 
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleWheel, handleMouseDown, handleMouseMove, handleTouchStart, handleTouchMove]);

  const handleDoubleClick = (e) => {
    if (e.target.closest('.menu-category-content')) return;
    e.preventDefault();
    if (loading) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const zoomStep = 1.2; // or any scale factor > 1 for zoom in

    // Calculate offsets relative to the center and current pan/zoom
    const offsetX = (mouseX - pan.x - rect.width / 2) / zoom;
    const offsetY = (mouseY - pan.y - rect.height / 2) / zoom;

    const newZoom = Math.min(10, zoom * zoomStep);
    const zoomRatio = newZoom / zoom;

    const newPan = {
      x: pan.x - offsetX * (zoomRatio - 1),
      y: pan.y - offsetY * (zoomRatio - 1),
    };

    setZoom(newZoom);
    setPan(newPan);
    setLastPan(newPan);
  };


  // const DRAG_THRESHOLD = 5;
  // const handleKuiperPointerDown = (e) => {
  //   isDraggingRef.current = true;
  //   dragMovedRef.current = false;
  //   dragStartYRef.current = e.clientY;
  //   scrollStartRef.current = kuiperListRef.current.scrollTop;
  //   kuiperListRef.current.setPointerCapture(e.pointerId);
  //   kuiperListRef.current.style.cursor = "grabbing";
  //   kuiperListRef.current.style.userSelect = "none";
  // };

  // const handleKuiperPointerMove = (e) => {
  //   if (!isDraggingRef.current) return;
  //   const deltaY = e.clientY - dragStartYRef.current;
  //   if (Math.abs(deltaY) > 5) {
  //     dragMovedRef.current = true;
  //   }
  //   kuiperListRef.current.scrollTop = scrollStartRef.current - deltaY;
  // };

  // const handleKuiperPointerUp = (e) => {
  //   isDraggingRef.current = false;
  //   kuiperListRef.current.releasePointerCapture(e.pointerId);
  //   kuiperListRef.current.style.cursor = "grab";
  //   kuiperListRef.current.style.userSelect = "auto";

  //   // since the container's pointer capture prevents native onClick from
  //   // ever reaching the child item.
  //   if (!dragMovedRef.current) {
  //     const target = e.target.closest('[data-kbo-name]');
  //     if (target) {
  //       const bodyName = target.getAttribute('data-kbo-name');
  //       const body = kuiperBodies.find((b) => b.name === bodyName);
  //       if (body) {
  //         handlePlanetClick(body);
  //         setQuickMenuOpen(false);
  //       }
  //     }
  //   }
  // };

  const DRAG_THRESHOLD = 5;

  const handleKuiperPointerDown = (e) => {
    isDraggingRef.current = true;
    dragMovedRef.current = false;
    dragStartYRef.current = e.clientY;
    scrollStartRef.current = kuiperListRef.current.scrollTop;
  };

  const handleKuiperPointerMove = (e) => {
    if (!isDraggingRef.current) return;

    const deltaY = e.clientY - dragStartYRef.current;

    if (!dragMovedRef.current && Math.abs(deltaY) > DRAG_THRESHOLD) {
      dragMovedRef.current = true;

      kuiperListRef.current.setPointerCapture(e.pointerId);
      kuiperListRef.current.style.cursor = "grabbing";
      kuiperListRef.current.style.userSelect = "none";
    }

    if (dragMovedRef.current) {
      kuiperListRef.current.scrollTop = scrollStartRef.current - deltaY;
    }
  };

  const handleKuiperPointerUp = (e) => {
    isDraggingRef.current = false;

    if (kuiperListRef.current.hasPointerCapture(e.pointerId)) {
      kuiperListRef.current.releasePointerCapture(e.pointerId);
    }

    kuiperListRef.current.style.cursor = "grab";
    kuiperListRef.current.style.userSelect = "none";
  };


  const getTouchDistance = (touches) => {
    const [a, b] = touches;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  };

  const animateZoomAndPan = (targetZoom, targetPan, duration = 500, onComplete) => {
    const startZoom = zoomRef.current;
    const startPan = panRef.current;

    const zoomDelta = targetZoom - startZoom;
    const panDelta = {
      x: targetPan.x - startPan.x,
      y: targetPan.y - startPan.y,
    };

    const startTime = performance.now();

    const animate = (time) => {
      const t = Math.min((time - startTime) / duration, 1);
      const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

      const currentZoom = startZoom + zoomDelta * ease;
      const currentPan = {
        x: startPan.x + panDelta.x * ease,
        y: startPan.y + panDelta.y * ease,
      };

      // Update refs for smooth rendering
      zoomRef.current = currentZoom;
      panRef.current = currentPan;

      // Throttled React update (every frame or every few frames — lightweight)
      setZoom(currentZoom);
      setPan(currentPan);

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        // Final state
        setZoom(targetZoom);
        setPan(targetPan);
        setLastPan(targetPan);
        zoomRef.current = targetZoom;
        panRef.current = targetPan;

        onComplete?.();
      }
    };

    requestAnimationFrame(animate);
  };

  const handlePlanetClick = (planet) => {
    if (!planet?.position || isTransitioning) return;

    const targetZoom = 15;
    const targetX = planet.position.x * AU_TO_PIXELS;
    const targetY = -planet.position.y * AU_TO_PIXELS;

    const targetPan = {
      x: -targetX * targetZoom,
      y: -targetY * targetZoom,
    };

    setIsTransitioning(true);           
    setSelectedPlanet(planet);         
    setShow3DContainer(true);          
    setIs3DViewEnabled(true);          
    setAre2DPlanetsVisible(false);     

    setTimeout(() => setFadeOutComplete(true), 1200); // adjust to your CSS timing

    // Start zoom/pan animation
    animateZoomAndPan(targetZoom, targetPan, 1000);

    // Allow transition again after all animations (3D fade-in etc.)
    setTimeout(() => setIsTransitioning(false), 3200); // conservative estimate
  };

  // const getScaledPlanetSize = (planet) => {
  //   const size = (PLANET_DIAMETERS[planet.name] || 1) * AU_TO_PIXELS * 0.8;
  //   return Math.min(10, size, 150);
  // };

  // const getScaledPlanetSize = (planet) => {
  //   const size = (PLANET_DIAMETERS[planet.name] || 1) * AU_TO_PIXELS * 0.8;
  //   const planetName = planet.name.toLowerCase();

  //   if (planetName === "moon") {
  //     return Math.max(2, Math.min(size, 13));
  //   }
  //   else if(planetName === "earth") {
  //     return Math.max(4, Math.min(size, 13));
  //   }
  //   return Math.max(3, Math.min(size, 13));
  // };

  const MIN_PLANET_SIZES = {
    earth: 4,
    moon: 2,
  };

  const MAX_PLANET_SIZE = 13;
  const DEFAULT_MIN_SIZE = 3;

  const getScaledPlanetSize = (planet) => {
    const size = (PLANET_DIAMETERS[planet.name] || 1) * AU_TO_PIXELS * 0.8;
    const minSize = MIN_PLANET_SIZES[planet.name.toLowerCase()] ?? DEFAULT_MIN_SIZE;

    return Math.max(minSize, Math.min(size, MAX_PLANET_SIZE));
  };

  const getOrbitalRadius = (planet) =>
    planet.position ? Math.hypot(planet.position.x, planet.position.y) * AU_TO_PIXELS : 0;

    const panLimits = useMemo(() => {
    const maxOrbit = Math.max(...Object.values(planetPositions).map(getOrbitalRadius)) + 100;
    const buffer = 200;
    const zoomedMax = (maxOrbit + buffer) * zoom;

    return {
      xMin: -zoomedMax,
      xMax: zoomedMax,
      yMin: -zoomedMax,
      yMax: zoomedMax,
    };
  }, [planetPositions, zoom]);

  const clampPan = (newPan) => ({
    x: Math.max(panLimits.xMin, Math.min(panLimits.xMax, newPan.x)),
    y: Math.max(panLimits.yMin, Math.min(panLimits.yMax, newPan.y)),
  });

  const sunX = sunPosition?.x || 0;
  const sunY = sunPosition?.y || 0;
  const sunOffsetX = sunX * AU_TO_PIXELS;
  const sunOffsetY = sunY * AU_TO_PIXELS;

  const restore2DView = useCallback(() => {
    // console.log("HELLO1");
    setIsTrackingSatellite(false);
    if (isTransitioningRef.current || !is3DViewEnabled) return;
    // console.log("HELLO2");

    isTransitioningRef.current = true; // lock right away
    setIsTransitioning(true);
    setIs3DFadingOut(true);
    setAre2DPlanetsVisible(true);
    cleanupAudioContext();
    setIsTrackingSatellite(false);
    setIsExploringMars(false);
    
    const targetZoom = 1;
    const targetPan = { x: 0, y: 0 };

    animateZoomAndPan(targetZoom, targetPan, 800, () => {
      // console.log("HELLO3");

      zoomRef.current = targetZoom;
      panRef.current = targetPan;
      setZoom(targetZoom);
      setPan(targetPan);
      setLastPan(targetPan);

      setShow3DContainer(false);
      setIs3DViewEnabled(false);
      setIs3DFadingOut(false);
      setSelectedPlanet(null);
      setFadeOutComplete(false);

      isTransitioningRef.current = false; // unlock
      setIsTransitioning(false);
      // console.log("hello4");
    });
  }, [is3DViewEnabled]);

  useEffect(() => {
    if (isTrackingSatellite) {
      setIs3DViewEnabled(false);
    }
  }, [isTrackingSatellite]);

  const [glowColor, setGlowColor] = useState(null);

  const getGlowColor = (k) => {
    if (k > 3000) return '#FFD700';
    if (k > 1000) return '#FF4500';
    if (k > 500) return '#FF6347';
    if (k > 300) return '#FFA500';
    if (k > 100) return '#87CEEB';
    return '#1E90FF';
  };

  const handleGlow = (kelvin) => {
    const color = getGlowColor(kelvin);
    setGlowColor(color);
    setTimeout(() => {
      setGlowColor(null);
    }, 2000);
  };

  // useEffect(() => {
  //   console.log("Glow color state updated:", glowColor);
  // }, [glowColor]);

  useEffect(() => {
    if (is3DViewEnabled && fadeOutComplete) {
      const timeout = setTimeout(() => {
        setEnableZoomTranslation(true);
      }, 10); // adjust delay as needed

      return () => clearTimeout(timeout);
    } else {
      setEnableZoomTranslation(false);
    }
  }, [is3DViewEnabled, fadeOutComplete]);

  const [currentUtcTime, setCurrentUtcTime] = useState(new Date().toISOString());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentUtcTime(new Date().toISOString());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (ready && selectedPlanet) {
      console.log("Rendering Planet3DRenderer for:", selectedPlanet.name);
    }
  }, [ready, selectedPlanet]);

  const handleRingClick = (ringName) => {
    console.log(`Ring clicked: ${ringName}`);
  };

  const [showPopup, setShowPopup] = useState(false);

  const handleCheckboxChange = (checked) => {
    setSkipAnimationSetting(checked); // update checkbox immediately
    localStorage.setItem('skipAnimation', checked ? 'true' : 'false'); // save for next reload
    // do NOT update skipAnimation here, it only affects future reloads
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setQuickMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      {glowColor && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 99999,
            animation: 'fadeOutGlow 2s forwards',
            background: `
              linear-gradient(
                to top, 
                ${glowColor} 0%, transparent 90px, transparent 50%
              ),
              linear-gradient(
                to left, 
                ${glowColor} 0%, transparent 90px, transparent 50%
              ),
              linear-gradient(
                to bottom, 
                ${glowColor} 0%, transparent 90px, transparent 50%
              ),
              linear-gradient(
                to right, 
                ${glowColor} 0%, transparent 90px, transparent 50%
              )
            `,
          }}
        />
      )}
      <style>{`
        @keyframes fadeOutGlow {
          0%   { opacity: 0.4; }
          10%  { opacity: 0.35; }
          20%  { opacity: 0.3; }
          30%  { opacity: 0.25; }
          40%  { opacity: 0.2; }
          50%  { opacity: 0.15; }
          60%  { opacity: 0.1; }
          70%  { opacity: 0.07; }
          80%  { opacity: 0.04; }
          90%  { opacity: 0.02; }
          95%  { opacity: 0.01; }
          98%  { opacity: 0.005; }
          100% { opacity: 0; }
        }
      `}</style>
      {!skipAnimation && !ready && (
        <>
          <PlanetPreloader 
          skipAnimation={skipAnimation}
          onComplete={(loadedAssets) => {
            console.log("Loaded planet assets:", loadedAssets);
            const {
              planetTextures,
              ringTextures,
              missionImages,
              backgroundImages,
              timelineImages,
              planetModel,
            } = normalizeAndSplitAssets(loadedAssets, [...planetAssets, ...earthAssets, ...missionAssets, ...backgroundAssets, ...timelineAssets]);
            setPlanetTextures(planetTextures);
            setRingTextures(ringTextures);
            setMissionImages(missionImages);
            setBackgroundImages(backgroundImages);
            setTimelineImages(timelineImages);
            setPlanetModel(planetModel);
            setReady(true);
            setLoading(false);
          }}/>
          <AsteroidPreloader
          skipAnimation={skipAnimation}
          onComplete={(loadedAssets) => {
            console.log("Loaded asteroid assets:", loadedAssets);
            const {
              asteroidGeometries,
            } = normalizeAndSplitAssets(
              loadedAssets,
              [...asteroidAssets]
            );
            setAsteroidGeometries(asteroidGeometries);
            setReady(true);
            setLoading(false);
          }}
          />
        </>             
      )}
    {(!skipAnimation && !milkywayAnimStarted) && (
      <div className="solarsystem-cover"></div>
    )} 
    <div className="solar-system-container" ref={containerRef}>
      <div className="solar-system" style={{
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        userSelect: isPanning ? 'none' : 'auto',
        touchAction: 'none',
        position: 'relative',
      }}>
        {/* <div className={`container-top-bar ${(is3DViewEnabled || isTrackingSatellite) ? 'hidden' : ''}  */}
         <div className={`container-top-bar ${(!are2DPlanetsVisible) ? 'hidden' : ''} 
          ${fadeOutComplete ? 'hidden-after-fade' : ''}`}>
          <div className="kp-index-box">
            <div className="utc-time">
              <span className='live-utc'>LIVE: </span>
              <span>{new Date(currentUtcTime).toUTCString()}</span>
              <span style={{
                position: 'absolute',
                right: '0.5rem',
              }}>
                <button
                  onClick={() => setShowPopup(!showPopup)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <svg width="22" height="22" fill="#e7dbb9" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 1a9 9 0 1 1 0 18A9 9 0 0 1 10 1zm0 2a7 7 0 1 0 0 14A7 7 0 0 0 10 3zm.9 10.5v-5h-1.8v5h1.8zM10 6.5a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8z"/>
                  </svg>
                </button>
              </span>
              {showPopup && (
                <div style={{
                  position: 'absolute',
                  top: '100%', // directly below container
                  // right: '1rem',
                  left: '0',
                  marginTop: '0.8rem',
                  backgroundColor: 'rgba(20, 20, 20, 0.9)',
                  color: '#e7dbb9',
                  padding: '1rem 1.2rem',
                  borderRadius: '10px',
                  fontFamily: 'Orbitron, sans-serif',
                  fontSize: '0.9rem',
                  maxWidth: '280px',
                  width: 'fit-content',
                  boxShadow: '0 0 12px rgba(0,0,0,0.4)',
                  zIndex: 999999,
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                  textAlign: 'left',
                }}>
                  <span style={{ fontWeight: 700 }}>Kp Index:</span><br />
                  The Kp index measures global geomagnetic activity on a scale from 0 (calm)
                  to 9 (extreme storm). Higher Kp values indicate stronger disturbances in Earth’s
                  magnetic field — which can make the 
                  <span style={{ fontWeight: 700 }}> Northern Lights (Aurora Borealis) </span> 
                  visible farther south than usual. It reflects how the solar wind interacts 
                  with Earth’s magnetosphere.
                </div>
              )}
            </div>
            <div><h4>Kp Index:</h4></div>
            <div className="kp-index">
              {typeof kpIndex === 'number' ? kpIndex.toFixed(2) : <span className="ai-thinking">AI Processing</span>}
            </div>
            <div className="retrieval-time">
              {KpRetrievalTime
                ? <>
                  <div className="mins-ago">Last updated: {formatDistanceToNow(new Date(KpRetrievalTime))} ago </div>
                  <div>(UTC: {new Date(KpRetrievalTime).toUTCString()})</div>
                </>
                : 'Retrieval time unknown'}
            </div>
          </div>
        </div>
        <div
          className="solar-system-content"
          // onDoubleClick={handleDoubleClick}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isPanning ? 'none' : 'transform 0.1s ease-out',
            height: '100%',
            width: '100%',
            position: 'relative',
            touchAction: 'none',
          }}
        >             
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 1,
              overflow: 'visible',
            }}
            viewBox={`${-containerSize.width / 2} ${-containerSize.height / 2} ${containerSize.width} ${containerSize.height}`}
          >
            {!show3DContainer && (
              <>
                {/* KUIPER BELT HIGHLIGHT */}
                {renderBeltHighlight({
                  sunOffsetX,
                  sunOffsetY,
                  outerR: kuiperOuterR,
                  innerR: kuiperInnerR,
                  fill: "rgba(117, 197, 255, 0.0375)",
                  textFill: "rgba(220, 240, 255, 0.065)",
                  fontSize: 150,
                  topScale: 0.725,
                  bottomScale: 0.875,
                  topLetterSpacing: 25,
                  bottomLetterSpacing: 40,
                  topText: "KUIPER",
                  bottomText: "BELT",
                  topId: "kuiper-belt-top",
                  bottomId: "kuiper-belt-bottom",
                })}
                {/* ASTEROID BELT HIGHLIGHT */}
                {renderBeltHighlight({
                  sunOffsetX,
                  sunOffsetY,
                  outerR: asteroidOuterR,
                  innerR: asteroidInnerR,
                  fill: "rgba(230, 174, 106, 0.03)",
                  textFill: "rgba(220, 205, 185, 0.3)",
                  fontSize: 8,
                  topScale: 0.725,
                  bottomScale: 0.75,
                  topLetterSpacing: 6,
                  bottomLetterSpacing: 8,
                  topText: "ASTEROID",
                  bottomText: "BELT",
                  topId: "asteroid-belt-top",
                  bottomId: "asteroid-belt-bottom",
                  bottomStartOffset: "53%",
                })}

              </>
            )}
            {/* Planetary orbits */}
            {Object.values(planetPositions)
              .filter((planet) => planet.name !== 'Sun')
              .map((planet) => {
                const points = computeOrbitPathAU(planet.name);
                if (!points.length) return null;

                const pathD = points
                  .map((pt, idx) => {
                    const px = pt.x * AU_TO_PIXELS - sunOffsetX;
                    const py = -pt.y * AU_TO_PIXELS + sunOffsetY;
                    return `${idx === 0 ? 'M' : 'L'} ${px} ${py}`;
                  })
                  .join(' ') + ' Z';

                return (
                  <path
                    key={`orbit-${planet.name}`}
                    d={pathD}
                    fill="none"
                    stroke="rgba(255, 255, 255, 0.51)"
                    strokeWidth={0.5}
                    strokeDasharray="4 4"
                    className={`orbital-line ${
                      !are2DPlanetsVisible ? 'faded-out' : 'visible'
                    } ${fadeOutComplete ? 'hidden-after-fade' : ''}`}
                  />
                );
              })}
          </svg>
          {Object.values(planetPositions).map((planet) => {
            const size = getScaledPlanetSize(planet);
            const x = (planet.position?.x ? planet.position.x * AU_TO_PIXELS : 0) - sunOffsetX;
            const y = (planet.position?.y ? -planet.position.y * AU_TO_PIXELS : 0) + sunOffsetY;
            const planetInfo = PLANET_DATA[planet.name];
            const isSun = planet.name === 'Sun';

            return (
              <div
                key={planet.name}
                className={`planet planet-${planet.name.toLowerCase()} ${
                  !are2DPlanetsVisible ? 'faded-out' : 'visible'
                } ${fadeOutComplete ? 'hidden-after-fade' : ''}`}
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  backgroundColor: planetInfo.color || 'gray',
                  position: 'absolute',
                  top: `calc(50% - ${size / 2}px + ${y}px)`,
                  left: `calc(50% - ${size / 2}px + ${x}px)`,
                  cursor: hovered === planet.name ? 'pointer' : 'default',
                  zIndex: isSun ? 1 : 10,
                }}
                onClick={() => handlePlanetClick(planet)}
                onMouseEnter={() => setHovered(planet.name)}
                onMouseLeave={() => setHovered(null)}
              >
                {hovered === planet.name && (
                  <div
                    className="planet-info"
                    style={{
                      transform: `translateX(-50%) scale(${1 / zoom})`,
                      transformOrigin: 'bottom center',
                    }}
                  >
                    <div className="planet-name">{planet.name}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <AsteroidBelt3D
          geometries={asteroidGeometries}
          innerR={asteroidInnerR}
          outerR={asteroidOuterR}
          sunOffsetX={sunOffsetX}
          sunOffsetY={sunOffsetY}
          containerSize={containerSize}
          zoom={zoom}
          pan={pan}
          visible={are2DPlanetsVisible && !show3DContainer}
        />
      </div>
      {!show3DContainer &&(
        <>
          <div className="quick-menu-container" ref={menuRef}>
           <button
              className={`quick-menu-toggle ${quickMenuOpen ? 'open' : ''}`}
              onClick={() => setQuickMenuOpen((prev) => !prev)}
              aria-label="Toggle quick menu"
            >
              <span className="kebab">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </span>
            </button>
            {quickMenuOpen && (
              <div className="planet-quick-menu">
                {/* PLANETS CATEGORY */}
                <div className="menu-category">
                  <div
                    className="menu-category-header"
                    onClick={() => {const next = !planetsOpen;
                      setPlanetsOpen(next);
                      if (next) {
                        setKuiperOpen(false);
                        // setSettingsOpen(false);
                      }
                    }}
                  >
                    <span>Planets</span>
                    <span>{planetsOpen ? "▾" : "▸"}</span>
                  </div>

                  {planetsOpen && (
                    <div className="menu-category-content">
                      {planets.map((planet) => (
                        <div
                          key={`menu-${planet.name}`}
                          className={`planet-menu-item ${
                            hovered === planet.name ? "hovered" : ""
                          }`}
                          onClick={() => {
                            handlePlanetClick(planet);
                            setQuickMenuOpen(false);
                          }}
                          onMouseEnter={() => setHovered(planet.name)}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <span
                            className="planet-menu-color"
                            style={{ backgroundColor: PLANET_DATA[planet.name].color }}
                          />
                          {planet.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* KUIPER BELT CATEGORY */}
                <div className="menu-category">
                  <div
                    className="menu-category-header"
                    onClick={() => {const next = !kuiperOpen;
                      setKuiperOpen(next);
                      if (next) {
                        setPlanetsOpen(false);
                        // setSettingsOpen(false);
                      }
                    }}
                  >
                    <span>Kuiper Belt</span>
                    <span>{kuiperOpen ? "▾": "▸"}</span>
                  </div>

                  {kuiperOpen && (
                    <div
                      ref={kuiperListRef}
                      className={`menu-category-content ${
                        kuiperBodies.length > 10 ? "scrollable-menu" : ""
                      }`}
                      onPointerDown={(e) => {                        
                        e.stopPropagation();
                        handleKuiperPointerDown(e);
                      }}
                      onPointerMove={(e) => {
                        e.stopPropagation();
                        handleKuiperPointerMove(e);
                      }}
                      onPointerUp={(e) => {
                        e.stopPropagation();
                        handleKuiperPointerUp(e);
                      }}
                      onPointerLeave={handleKuiperPointerUp}
                      onWheel={(e) => e.stopPropagation()}
                      style={{ cursor: "grab", touchAction: "pan-y" }}
                    >
                      {kuiperBodies.map((body) => (
                        <div
                          key={`menu-${body.name}`}
                          className={`planet-menu-item ${
                            hovered === body.name ? "hovered" : ""
                          }`}
                          onClick={() => {
                            if (dragMovedRef.current) return;
                            handlePlanetClick(body);
                            setQuickMenuOpen(false);
                          }}
                          onMouseEnter={() => setHovered(body.name)}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <span
                            className="planet-menu-color"
                            style={{
                              backgroundColor: PLANET_DATA[body.name].color,
                            }}
                          />
                          {body.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SETTINGS CATEGORY */}
                <div className="menu-category">
                  <div
                    className="menu-category-header"
                    onClick={() => setSettingsOpen((prev) => !prev)}
                  >
                    <span>Settings</span>
                    <span>{settingsOpen ? "▾" : "▸"}</span>
                  </div>

                  {settingsOpen && (
                    <div className="menu-category-content" 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem' 
                        }}>
                      <label 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem', 
                          cursor: 'pointer' 
                          }}>
                        <input
                          type="checkbox"
                          checked={skipAnimationSetting}
                          onChange={(e) => handleCheckboxChange(e.target.checked)}
                          style={{ 
                            width: '0.9rem', 
                            height: '0.9rem', 
                            margin: 0, 
                            padding: 0, 
                            accentColor: '#e7dbb9' 
                          }}
                        />
                        Skip animation
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
        {show3DContainer && selectedPlanet && ready &&(
          <>
           <div className={`three-d-container ${is3DFadingOut ? 'three-d-fade-out' : ''}`} 
              style={{touchAction: is3DViewEnabled ? 'none' : 'auto',}}>
            <Planet3DRenderer
              planet={selectedPlanet}
              planetModel= {planetModel}
              texture={planetTextures[selectedPlanet.name.toLowerCase()]}  // The planet texture
              ringTextures={ringTextures}
              timelineImages={timelineImages}
              zoom={zoom}
              pan={pan}
              position={[
                selectedPlanet.position.x * AU_TO_PIXELS,
                -selectedPlanet.position.y * AU_TO_PIXELS,
                0
              ]}
              isExpanded={isExpanded}
              onRingClick={handleRingClick}
              is3DViewEnabled={is3DViewEnabled}
              setIs3DViewEnabled={setIs3DViewEnabled}
              isTrackingSatellite ={isTrackingSatellite}
              setIsTrackingSatellite={setIsTrackingSatellite}
              isExploringIss={isExploringIss}
              setIsExploringIss={setisExploringIss}
              isExploringHst={isExploringHst}
              setIsExploringHst={setisExploringHst}
              isExploringMars={isExploringMars}
              setIsExploringMars={setIsExploringMars}

            />
          </div>
          </>
        )}       
    </div>    
    <div>
      {(loading && !ready) ? (
          <div className="loading-screen">
            <div className="loading-message">Loading planetary system...</div>
            <div className="progress-bar">
              <div className="progress" style={{ width: `${loadProgress}%` }}></div>
            </div>
            <div className="loading-percent">{loadProgress}%</div>
          </div>
        ) : (
          <Suspense fallback={<div />}>
            {!skipAnimation &&
              <MilkyWayIntro 
                assets={backgroundImages}
                skipAnimation={skipAnimationSetting}
                onSkipChange={handleCheckboxChange}
                onAnimationStart={() => setMilkywayAnimStarted(true)}
              />}
          </Suspense>          
        )}
      <div className={`zoom-controls ${enableZoomTranslation ? 'translate-up' : ''} ${enableZoomTranslation && isExpanded ? 'expanded' : ''}`}>
        {(are2DPlanetsVisible) && (
          <>
            <button onClick={() => handleZoomByStep(-1)}>＋</button>
            <button onClick={() => handleZoomByStep(1)}>−</button>
          </>            
        )}
        {(!is3DViewEnabled && !isTrackingSatellite && are2DPlanetsVisible) &&(
          <button onClick={handleResetView}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"/>
                <path d="M8 1.5a.5.5 0 0 0-.5.5v3.793L5.354 4.146a.5.5 0 1 0-.708.708l3 3 .007.007a.498.498 0 0 0 .7-.007l3-3a.5.5 0 1 0-.708-.708L8.5 5.793V2a.5.5 0 0 0-.5-.5z"/>
            </svg>
          </button>
        )}
        {(is3DViewEnabled)&& (
          <button onClick={handleResetView}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H3.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L3.707 7.5H14.5a.5.5 0 0 1 .5.5z"/>
            </svg>
          </button>
        )}
      </div>
      <div> 
        {(selectedPlanet && is3DViewEnabled)  && (
          <PlanetDetailCard
            key={selectedPlanet ? selectedPlanet.name : 'none'}
            selectedPlanet={selectedPlanet}
            missionAssets={missionImages}
            // onClose={() => setSelectedPlanet(null)}
            // onClose={restore2DView}
            onClose={handleResetView}
            is3DViewEnabled={is3DViewEnabled}
            is3DFadingOut={is3DFadingOut}
            fadeOutComplete={fadeOutComplete}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            onGlow={handleGlow}
            isTrackingSatellite={isTrackingSatellite}
            setIsTrackingSatellite={setIsTrackingSatellite}
            setIsExploringMars={setIsExploringMars}
          />
        )}
      </div>      
    </div>
    </>
  );
}
export default SolarSystem;

function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}
function getDynamicScale() {
  const width = window.innerWidth;
  if (width < 480) return 0.3;
  if (width < 768) return 0.5;
  if (width < 1024) return 0.7;
  return 1;
}
